// Йотированные формы гласных: после палатализованного согласного и после j.
const IOTATED_VOWELS: Record<string, string> = {
  a: 'я',
  ɑ: 'я',
  ɐ: 'я',
  ʌ: 'я',
  æ: 'е',
  e: 'е',
  ɛ: 'е',
  ə: 'е',
  ɘ: 'е',
  ɜ: 'е',
  i: 'и',
  ɪ: 'и',
  o: 'ё',
  ɔ: 'ё',
  ɵ: 'ё',
  ɞ: 'ё',
  u: 'ю',
  ʊ: 'ю',
  ʉ: 'ю',
  y: 'ю',
  ʏ: 'ю',
  ɨ: 'ы',
  ɯ: 'ы',
}

// Обычные формы гласных.
const PLAIN_VOWELS: Record<string, string> = {
  a: 'а',
  ɑ: 'а',
  ɐ: 'а',
  ʌ: 'а',
  æ: 'э',
  e: 'е',
  ɛ: 'е',
  ə: 'е',
  ɘ: 'е',
  ɜ: 'е',
  i: 'и',
  ɪ: 'и',
  o: 'о',
  ɔ: 'о',
  ɒ: 'о',
  ɤ: 'о',
  ɵ: 'о',
  ɞ: 'о',
  u: 'у',
  ʊ: 'у',
  ʉ: 'у',
  y: 'ю',
  ʏ: 'ю',
  ø: 'ё',
  œ: 'ё',
  ɶ: 'ё',
  ɨ: 'ы',
  ɯ: 'ы',
}

const AFFRICATES: Record<string, string> = {
  dʑ: 'дж',
  dʒ: 'дж',
  dz: 'дз',
  ts: 'ц',
  tɕ: 'ч',
  tʃ: 'ч',
}

const CONSONANTS: Record<string, string> = {
  b: 'б',
  p: 'п',
  d: 'д',
  t: 'т',
  g: 'г',
  ɡ: 'г',
  k: 'к',
  q: 'к',
  v: 'в',
  f: 'ф',
  z: 'з',
  s: 'с',
  m: 'м',
  n: 'н',
  l: 'л',
  r: 'р',
  h: 'х',
  x: 'х',
  χ: 'х',
  ħ: 'х',
  ʕ: 'х',
  ɦ: 'х',
  ʜ: 'х',
  ɣ: 'г',
  ɢ: 'г',
  ɠ: 'г',
  ʛ: 'г',
  β: 'б',
  ɸ: 'ф',
  θ: 'т',
  ð: 'д',
  ɖ: 'д',
  ʈ: 'т',
  ɱ: 'м',
  ɳ: 'н',
  ɾ: 'р',
  ɽ: 'р',
  ɹ: 'р',
  ɻ: 'р',
  ʀ: 'р',
  // Увулярный: в эталонных русских передачах армянской ղ даётся как «г».
  ʁ: 'г',
  ʙ: 'р',
  ⱱ: 'р',
  ɺ: 'л',
  ɭ: 'л',
  ʟ: 'л',
  ɬ: 'л',
  ɮ: 'л',
  ʃ: 'ш',
  ʂ: 'ш',
  ɧ: 'ш',
  ʒ: 'ж',
  ʐ: 'ж',
  ʑ: 'ж',
  ɕ: 'щ',
  ŋ: 'н',
  ɴ: 'н',
  ⁿ: 'н',
  ʋ: 'в',
  ɰ: 'в',
  w: 'в',
  ʍ: 'в',
  ʔ: "'",
  ʡ: "'",
  ˀ: "'",
  ɓ: 'б',
  ɗ: 'д',
  ʄ: 'дж',
  ʦ: 'ц',
  ʣ: 'дз',
  ʧ: 'ч',
  ʤ: 'дж',
  ʨ: 'ч',
  ʥ: 'дж',
  // Палатальные согласные: сами по себе мягкие (см. IMPLICIT_PALATAL).
  c: 'к',
  ɟ: 'д',
  ɲ: 'н',
  ʎ: 'л',
  ç: 'х',
}

// Базовые кириллические буквы, которые в русской графике не смягчаются:
// знак палатализации перед ними теряется.
const ALWAYS_HARD_BASES = new Set(['ш', 'ж', 'ц'])

// Всегда мягкие буквы: йотированные гласные после них не нужны (ча, а не чя).
const INHERENTLY_SOFT_BASES = new Set(['ч', 'щ'])

// Согласные IPA, которые обозначают мягкий звук без отдельного знака палатализации.
const IMPLICIT_PALATAL = new Set(['c', 'ç', 'ɟ', 'ɲ', 'ʎ'])

const AFFRICATE_KEYS = Object.keys(AFFRICATES).sort((a, b) => b.length - a.length)

function normalizeIpa(ipa: string): string {
  return ipa
    .replace(
      /[\u0300-\u036f\u1ab0-\u1aff\u1dc0-\u1dff\u20d0-\u20ff\u2de0-\u2dff\ufe20-\ufe2f\u200d\u02b0\u02b1\u02b3-\u02c7\u02ce-\u02ff]/g,
      ''
    )
    .toLowerCase()
}

function uppercaseFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function isLetter(ch: string): boolean {
  return /\p{L}/u.test(ch)
}

// На слух автора редуцированный ə на стыке слова ближе всего к «э».
function cyrillicVowel(ch: string, prev: string | undefined, next: string | undefined): string {
  if (ch === 'ə' && (prev === undefined || !isLetter(prev) || next === undefined || !isLetter(next))) {
    return 'э'
  }
  return PLAIN_VOWELS[ch]
}

export function ipaToCyrillic(ipa: string): string {
  const uppercase = ipa.length > 0 && ipa[0] !== ipa[0].toLowerCase()
  const normalized = normalizeIpa(ipa)
  let result = ''
  let i = 0
  while (i < normalized.length) {
    let matchedAffricate = false
    for (const key of AFFRICATE_KEYS) {
      if (normalized.startsWith(key, i)) {
        result += AFFRICATES[key]
        i += key.length
        matchedAffricate = true
        break
      }
    }
    if (matchedAffricate) continue

    const ch = normalized[i]
    if (ch === 'ʲ') {
      // Знак палатализации без согласной слева не влияет на запись.
      i++
      continue
    }
    if (ch === 'j') {
      const next = normalized[i + 1]
      const iotated = next ? IOTATED_VOWELS[next] : undefined
      if (iotated !== undefined) {
        result += iotated
        i += 2
      } else {
        result += 'й'
        i++
      }
      continue
    }

    const vowel = PLAIN_VOWELS[ch]
    if (vowel !== undefined) {
      result += cyrillicVowel(ch, normalized[i - 1], normalized[i + 1])
      i++
      continue
    }

    const base = CONSONANTS[ch]
    if (base !== undefined) {
      let j = i + 1
      const palatalized = IMPLICIT_PALATAL.has(ch) || normalized[j] === 'ʲ'
      if (normalized[j] === 'ʲ') j++
      const next = normalized[j]
      const nextIotated = next ? IOTATED_VOWELS[next] : undefined
      if (nextIotated !== undefined) {
        const softVowel =
          !palatalized || ALWAYS_HARD_BASES.has(base) || INHERENTLY_SOFT_BASES.has(base)
            ? cyrillicVowel(next, ch, normalized[j + 1])
            : nextIotated
        result += base + softVowel
        i = j + 1
        continue
      }
      const needsSoftSign = palatalized && !ALWAYS_HARD_BASES.has(base) && !INHERENTLY_SOFT_BASES.has(base)
      result += needsSoftSign ? base + 'ь' : base
      i = j
      continue
    }

    if (ch === 'ˈ') {
      result += "'"
      i++
      continue
    }
    result += ch
    i++
  }
  return uppercase ? uppercaseFirst(result) : result
}
